'use client'

import { motion } from 'motion/react'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IDetailRestaurant } from '@/api/restaurants/types/restaurant-types'
import { Button } from '@/components/button'
import { Skeleton } from '@/components/skeleton'
import { SvgOutlinedChevronRight } from '@/icons/svg-outlined-chevron-right'
import { range } from '@/utils/functions/range'
import { throttle } from '@/utils/functions/throttle'
import { createImageUrl } from '@/utils/image/create-image-url'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'
import { RestaurantDetailVideo } from './restaurant-detail-video'

interface IRestaurantDetailVisualsContainerProps {
    visuals: IDetailRestaurant['visuals'] | null
    className?: string
}

/**
 * Rendered height of the gallery row on desktop, also used as the CDN resize
 * request (which doubles it for retina). Requesting only a height keeps each
 * visual at its intrinsic aspect ratio.
 */
const VISUAL_REQUEST_HEIGHT = 400

/**
 * Free-scrolling gallery row: every image and video keeps its own aspect
 * ratio at a fixed row height. No snapping, no pagination dots, desktop
 * chevrons scroll a chunk at a time.
 */
export const RestaurantDetailVisualsContainer = ({
    visuals,
    className,
}: IRestaurantDetailVisualsContainerProps) => {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)

    const [{ isAtStart, isAtEnd }, setScrollEdges] = useState({ isAtStart: true, isAtEnd: true })

    const updateScrollEdges = useCallback(() => {
        const container = scrollContainerRef.current

        if (!container) return

        const maxScrollLeft = container.scrollWidth - container.clientWidth

        setScrollEdges({
            isAtStart: container.scrollLeft <= 1,
            isAtEnd: container.scrollLeft >= maxScrollLeft - 1,
        })
    }, [])

    const throttledUpdateScrollEdges = useMemo(
        () => throttle(updateScrollEdges, 150),
        [updateScrollEdges]
    )

    useEffect(() => {
        const container = scrollContainerRef.current

        if (!container) return

        updateScrollEdges()

        // Tile widths only become known as media loads, so the overflow state
        // changes after mount. Load events don't bubble but they do capture.
        container.addEventListener('load', updateScrollEdges, true)
        container.addEventListener('loadedmetadata', updateScrollEdges, true)
        window.addEventListener('resize', updateScrollEdges)

        return () => {
            container.removeEventListener('load', updateScrollEdges, true)
            container.removeEventListener('loadedmetadata', updateScrollEdges, true)
            window.removeEventListener('resize', updateScrollEdges)
        }
    }, [updateScrollEdges])

    const scrollByChunk = useCallback(
        (direction: 'next' | 'previous') => () => {
            const container = scrollContainerRef.current

            if (!container) return

            const chunk = container.clientWidth * 0.6

            container.scrollBy({
                behavior: 'smooth',
                left: direction === 'next' ? chunk : -chunk,
            })
        },
        []
    )

    const firstVideoIndex = visuals?.findIndex((visual) => visual.type === 'VIDEO')

    return (
        <div className={cn('relative w-full max-w-[100vw]', className)}>
            <motion.div
                initial={{ opacity: isAtStart ? 0 : 1 }}
                animate={{ opacity: isAtStart ? 0 : 1 }}
                transition={{ duration: 0.1, ease: 'easeOut' }}
                className="hidden md:block absolute transform-center-y left-10 z-[1] w-fit shadow-custom rounded-full"
                style={{ cursor: isAtStart ? 'default' : 'pointer' }}
            >
                <div className="rotate-180">
                    <Button
                        shape="circle"
                        styleType="secondary"
                        backgroundColor="white"
                        disabled={isAtStart}
                        onClick={scrollByChunk('previous')}
                        height={'48px'}
                        width={'48px'}
                        className="hidden md:flex"
                    >
                        <SvgOutlinedChevronRight color={COLORS.black} size={32} />
                    </Button>
                </div>
            </motion.div>

            <div
                ref={scrollContainerRef}
                onScroll={throttledUpdateScrollEdges}
                className="flex h-[260px] md:h-[400px] gap-s px-l md:px-2xl overflow-x-auto overflow-y-hidden
                    scrollbar-hide"
            >
                {!visuals?.length
                    ? range(4).map((_, index) => (
                          <Skeleton
                              key={index + 'restaurant-visual-skeleton'}
                              className="h-full w-[220px] md:w-[340px] shrink-0 rounded-l"
                          />
                      ))
                    : visuals.map((visual, index) => {
                          if (visual.type === 'VIDEO') {
                              return (
                                  <RestaurantDetailVideo
                                      key={visual.visualUrl + index}
                                      url={visual.visualUrl}
                                      thumbnailUrl={visual.thumbnailUrl}
                                      autoPlay={index === firstVideoIndex}
                                  />
                              )
                          }

                          return (
                              <div
                                  key={visual.visualUrl + index}
                                  className="h-full shrink-0 overflow-hidden rounded-l bg-bg-neutral-surface"
                              >
                                  <Image
                                      unoptimized
                                      priority={index < 4}
                                      src={createImageUrl(visual.visualUrl, {
                                          height: VISUAL_REQUEST_HEIGHT,
                                      })}
                                      alt="Restaurant image"
                                      height={0}
                                      width={0}
                                      className="h-full w-auto object-cover"
                                  />
                              </div>
                          )
                      })}
            </div>

            <motion.div
                initial={{ opacity: isAtEnd ? 0 : 1 }}
                animate={{ opacity: isAtEnd ? 0 : 1 }}
                transition={{ duration: 0.1, ease: 'easeOut' }}
                className="hidden md:block absolute transform-center-y right-10 z-[1] w-fit shadow-custom rounded-full"
                style={{ cursor: isAtEnd ? 'default' : 'pointer' }}
            >
                <Button
                    shape="circle"
                    styleType="secondary"
                    backgroundColor="white"
                    disabled={isAtEnd}
                    onClick={scrollByChunk('next')}
                    height={'48px'}
                    width={'48px'}
                    className="hidden md:flex"
                >
                    <SvgOutlinedChevronRight color={COLORS.black} size={32} />
                </Button>
            </motion.div>
        </div>
    )
}
